/**
 * =========================================================================================
 * Component - UAssignUser - Assign Users (Account Module)
 * =========================================================================================
 * Adapted from admin/src/common/components/UAssignUser
 * Supports leftTitle / rightTitle props for custom panel labels.
 **/

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as Icon from 'ubs-icons';
import UAvatar from 'ubs-framework/v2/UAvatar';
import UModal from 'ubs-framework/v2/UModal/NewUModal';
import UCustomScrollBar from 'ubs-framework/v2/UCustomScrollBar';
import UButton from 'ubs-framework/v2/UButton';
import NoDataFound from 'ubs-framework/v2/no-data-found';
import UAssignHistory from '../UAssignHistory';

/* ======= small helpers (no external deps beyond framework) ======= */
const COLOR = { u_font: '#73788b', u_blue: '#007AFF', u_icon_bg: '#73788b' };

/* ======= Simple list skeleton ======= */
const LSkeleton = () => (
    <div className="u_skeleton-list u_p-16">
        {[1, 2, 3].map(i => (
            <div key={i} className="u_skeleton-item u_mb-10 u_flex u_align-center">
                <div className="u_skeleton u_round-radius u_width-34 u_h-34" />
                <div className="u_skeleton u_ml-10 u_flex-1" style={{ height: 16, borderRadius: 4 }} />
            </div>
        ))}
    </div>
);

/* ======= User Group List ======= */
const UserGroupList = React.memo((props) => {
    const [open, setOpen] = useState(props?.index === 0);

    const count = useMemo(() => {
        let i = 0;
        props?.list?.forEach(e => {
            if (props?.column === 'right' ? props?.assigned.includes(e?._id) : !props?.assigned.includes(e?._id)) i++;
        });
        return i;
    }, [props?.list, props?.assigned, props?.column]);

    const handleOpen = () => {
        if (props?.groupBy) setOpen(!open);
    };

    useEffect(() => {
        setOpen(props?.groupBy ? props?.index === 0 : true);
    }, [props?.groupBy]);

    return (
        <>
            <div className={`u_assign-list__header ${props?.groupBy ? 'u_assign-list__header-group' : ''} ${count === 0 ? 'u_event-none' : ''}`}>
                <div className={`u_dashboard-title ${props?.groupBy ? 'u_cp' : ''}`} onClick={handleOpen}>
                    <span className="u_one-line">{props?.groupName}</span>
                    <p>({count})</p>
                </div>
                <div className="u_flex u_align-center">
                    <span className={`u_assign-list__header-btn ${props?.column === 'left' && props?.isNonEss && props?.list?.every(u => u.isAdmin) ? 'u_event-none u_opacity-50' : ''}`} onClick={() => {
                        let toAdd = props?.list;
                        if (props?.column === 'left' && props?.isNonEss) {
                            toAdd = props?.list?.filter(u => !u.isAdmin);
                        }
                        props?.onClick(toAdd);
                    }}>
                        {props?.column === 'right' ? 'Remove All' : 'Add All'}
                    </span>
                    {props?.groupBy && (
                        <i className="u_ml-10" onClick={handleOpen}>
                            <Icon.DownArrow fill={COLOR?.u_font} size={14} />
                        </i>
                    )}
                </div>
            </div>

            <div className={`u_assign-list__employees ${open ? 'active' : ''}`}>
                {open && props?.list?.map(user => {
                    if (props?.column === 'right' ? props?.assigned.includes(user?._id) : !props?.assigned.includes(user?._id)) {
                        return (
                            <UserListItem
                                key={user?._id}
                                userId={user?._id}
                                userName={user?.name}
                                empId={user?.empId || user?.employeeId}
                                department={user?.departmentName || user?.department}
                                profileImage={user?.avatarURL}
                                active={user?.isActive}
                                assigned={props?.assigned.includes(user?._id)}
                                isAdmin={user?.isAdmin}
                                disabled={props?.column === 'left' && props?.isNonEss && user?.isAdmin}
                                onClick={() => props?.onClick([user])}
                            />
                        );
                    }
                    return null;
                })}
            </div>
        </>
    );
});

UserGroupList.defaultProps = {
    index: 0,
    groupName: '',
    list: [],
    column: 'right',
    assigned: [],
    onClick: () => { },
};

/* ======= Individual User List Item ======= */
const UserListItem = React.memo((props) => {
    return (
        <div className={`u_assign-employee ${props?.active ? '' : 'u_assign-employee-inactive'}`}>
            <div className="u_ae__name">
                <UAvatar name={props?.userName} src={props?.profileImage} />
                <div className="u_ae__info">
                    <div className="u_ae__info-name">
                        <p className="u_one-line u_table-one-line">{props?.userName}</p>
                        {props?.isAdmin && (
                            <span className="u_badge-label u_badge-label__green u_font-medium u_ml-10">System Admin</span>
                        )}
                    </div>
                    {(props?.department || props?.empId) && (
                        <span className="u_ae__info-job">
                            {props?.department}{props?.empId ? ` - ${props.empId}` : ''}
                        </span>
                    )}
                </div>
            </div>
            <button
                className={`u_ae__emp-action ${props?.assigned ? 'u_ae__emp-remove' : 'u_ae__emp-add'} ${props?.disabled ? 'u_event-none u_opacity-50' : ''}`}
                onClick={() => !props?.disabled && props?.onClick()}
                disabled={props?.disabled}
                title={props?.disabled ? "Admin users cannot be assigned to Non-ESS" : ""}
            >
                {props?.assigned
                    ? <Icon.Delete fill="#FF6961" size="14" />
                    : <Icon.Plus fill="#FFFFFF" size="10" />
                }
            </button>
        </div>
    );
});

UserListItem.defaultProps = {
    userName: '',
    profileImage: '',
    assigned: false,
    active: true,
    onClick: () => { },
};

/* ======= Main UAssignUser Component ======= */
const UAssignUser = React.memo((props) => {
    const [groupBy] = useState(null);
    const [search, setSearch] = useState('');
    const [userList, setUserList] = useState([]);
    const [userTab, setUserTab] = useState('left');
    const [assigned, setAssigned] = useState([...props?.assignedUser]);
    const [dataLoading, setDataLoading] = useState(props?.loading);
    const [showHistory, setShowHistory] = useState(false);

    // Debounced search logic
    useEffect(() => {
        const timer = setTimeout(() => {
            if (props.onSearch) {
                props.onSearch(search);
            }
        }, 500); // 500ms debounce
        return () => clearTimeout(timer);
    }, [search]);

    const handle = {
        addUser: useCallback((data) => {
            const isNonEss = props?.rightTitle?.toLowerCase()?.includes('non ess');
            let toAdd = data;
            if (isNonEss) {
                toAdd = data?.filter(u => !u.isAdmin);
            }
            if (!toAdd?.length) return;
            const ids = toAdd?.map(e => e?._id);
            setAssigned(prev => [...prev, ...ids]);
        }, [props?.rightTitle]),
        removeUser: useCallback((data) => {
            const ids = data?.map(e => e?._id);
            setAssigned(prev => prev.filter(e => !ids.includes(e)));
        }, []),
        save: () => {
            const newAssign = assigned?.filter(x => !props?.assignedUser?.includes(x));
            const removeAssign = props?.assignedUser?.filter(x => !assigned?.includes(x));
            props.save({ newAssign, removeAssign, _id: props?._id, module: props?.module });
        },
    };

    useEffect(() => {
        setAssigned([...props?.assignedUser]);
    }, [props?.assignedUser]);

    useEffect(() => {
        if (!props?.loading) {
            const filtered = props?.users?.filter(u => {
                const name = u?.name?.toLowerCase() || '';
                const param = search?.toLowerCase() || '';
                return param.length ? name.includes(param) : true;
            });

            const grouped = filtered.reduce((acc, u) => {
                const key = groupBy ? u[groupBy] : 'all';
                acc[key] = [...(acc[key] || []), u];
                return acc;
            }, {});

            const result = Object.keys(grouped).map(key => ({
                group: key === 'all' ? null : key,
                list: grouped[key],
            }));

            setUserList(result);
            setDataLoading(false);
        }
    }, [groupBy, search, props?.users, props?.loading]);

    const leftTitle = props?.leftTitle || 'Unassigned';
    const rightTitle = props?.rightTitle || 'Assigned';

    return (
        <UModal
            open={true}
            maxWidth={900}
            className="u_assign_users u_sidebar-modal"
            onClose={() => props?.close()}
            position={'right'}
            header={
                <div className="u_assign-header u_flex u_flex-align u_content-between">
                    <div className="u_assign-header__title">
                        <h4>Assign Users</h4>
                        <p className="u_mt-5 u_one-line">{props?.header} {props?.name ? `- ${props?.name}` : ''}</p>
                    </div>
                    <div className="u_assign-header__filters u_flex u_flex-align">
                        <div className="u_flex u_align-center u_b-light u_radius-5 u_px-10 u_py-6">
                            <Icon.Search fill={COLOR.u_icon_bg} size={14} />
                            <input
                                className="u_ml-6 u_small-font"
                                placeholder="Search..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                style={{ border: 'none', outline: 'none', background: 'transparent', width: 120 }}
                            />
                        </div>
                        <div
                            className="u_icon-rollover u_filters-icon u_small-btn u_b-light u_ml-10 u_cp"
                            onClick={() => {
                                setShowHistory(true);
                                props?.onHistory && props.onHistory();
                            }}
                            title="View Activity Log"
                        >
                            <Icon.History fill={COLOR.u_icon_bg} size={18} />
                        </div>
                    </div>
                </div>
            }
            footer={
                <div className="u_row u_pl-20 u_pr-20   ">
                    <div className="u_col-7" ></div>
                    <div className="u_col-5 u_flex u_content-between" >
                            <UButton variant="white" type="cancel" onClick={() => props.close()} />
                            <UButton variant="blue" type="save" onClick={handle.save} />
                    </div>
                </div>
            }
        >
            <div className="u_assign-emp-body">
                <div className="u_assign-tab u_route-tabs u_bb-light">
                    <ul className="u_flex u_align-center">
                        <li onClick={() => setUserTab('left')}>
                            <span className={`${userTab === 'left' ? 'active' : ''}`}>{leftTitle}</span>
                        </li>
                        <li onClick={() => setUserTab('right')}>
                            <span className={`${userTab === 'right' ? 'active' : ''}`}>{rightTitle}</span>
                        </li>
                    </ul>
                </div>

                <div className="u_flex u_h-full">
                    {/* Left Panel */}
                    <div className={`u_assign-list u_assign-list-left ${userTab === 'left' ? 'active' : ''}`}>
                        <UCustomScrollBar>
                            {dataLoading ? <LSkeleton /> :
                                <>
                                    {userList.map((list, i) =>
                                        <UserGroupList
                                            key={i}
                                            index={i}
                                            column="left"
                                            list={list?.list}
                                            assigned={assigned}
                                            groupName={list?.group || leftTitle}
                                            onClick={handle.addUser}
                                            groupBy={groupBy}
                                            isNonEss={rightTitle?.toLowerCase()?.includes('non ess')}
                                        />
                                    )}
                                    {userList?.length === 0 && <NoDataFound />}
                                </>
                            }
                        </UCustomScrollBar>
                    </div>

                    {/* Right Panel */}
                    <div className={`u_assign-list u_assign-list-right ${userTab === 'right' ? 'active' : ''}`}>
                         <UCustomScrollBar>
                            {dataLoading ? <LSkeleton /> :
                                <>
                                    {userList.map((list, i) =>
                                        <UserGroupList
                                            key={i}
                                            index={i}
                                            column="right"
                                            list={list?.list}
                                            assigned={assigned}
                                            groupName={list?.group || rightTitle}
                                            onClick={handle.removeUser}
                                            groupBy={groupBy}
                                        />
                                    )}
                                    {assigned?.length === 0 && <NoDataFound />}
                                </>
                            }
                        </UCustomScrollBar>
                    </div>
                </div>
            </div>
            {showHistory && (
                <UAssignHistory
                    open={showHistory}
                    close={() => setShowHistory(false)}
                    socketData={props?.historyData}
                    loading={props?.historyLoading}
                    onFilter={(userId) => {
                        const payload = {};
                        if (userId) payload.user = userId;
                        props?.onHistory && props.onHistory(payload);
                    }}
                    onPagination={(payload) => props?.onHistory && props.onHistory(payload)}
                />
            )}
        </UModal>
    );
});


export default UAssignUser;
